// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a file's icon by its type, for chat file cards and the file preview window's header.

import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {FileTypeLogo, fileLogoKind} from '@app/features/ui/components/icons/FileTypeLogo';
import {
	FileArchiveIcon,
	FileAudioIcon,
	FileCodeIcon,
	FileIcon,
	FileImageIcon,
	FilePdfIcon,
	FilePptIcon,
	FileTextIcon,
	FileVideoIcon,
	FileXlsIcon,
} from '@app/features/ui/icons/Icons';

/** The icon in a file card: the app's logo for Word, Excel, PowerPoint and PDF, else an outline icon by type. */
export function FileTypeIcon({
	fileName,
	contentType,
	size: sizePx = 22,
}: {
	fileName: string;
	contentType?: string | null;
	/** The outline icon's size in px; logos are a little larger. Default for the 40px tile of a file card. */
	size?: number;
}) {
	const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
	const size = remFromPx(sizePx);
	// Word, Excel, PowerPoint and PDF files show the app's logo, a little larger than outline icons.
	const logo = fileLogoKind(fileName, contentType);
	if (logo) return <FileTypeLogo kind={logo} size={remFromPx(Math.round(sizePx * (28 / 22)))} />;
	const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
	const textTypes = ['txt', 'rtf', 'md', 'log'];
	const archiveTypes = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'];
	const audioTypes = ['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac', 'wma'];
	const videoTypes = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'flv', 'wmv'];
	const codeTypes = [
		'js',
		'jsx',
		'ts',
		'tsx',
		'py',
		'java',
		'c',
		'cpp',
		'h',
		'css',
		'html',
		'json',
		'xml',
		'yml',
		'yaml',
		'sh',
		'go',
		'rs',
		'rb',
		'php',
	];
	const excelTypes = ['xls', 'xlsx', 'csv'];
	const presentationTypes = ['ppt', 'pptx'];
	const documentTypes = ['doc', 'docx'];
	if (imageTypes.includes(fileExtension))
		return (
			<FileImageIcon
				size={size}
				data-flx="channel.embeds.attachments.attachment-file.get-file-type-icon.file-image-icon"
			/>
		);
	if (fileExtension === 'pdf')
		return (
			<FilePdfIcon size={size} data-flx="channel.embeds.attachments.attachment-file.get-file-type-icon.file-pdf-icon" />
		);
	if (textTypes.includes(fileExtension))
		return (
			<FileTextIcon
				size={size}
				data-flx="channel.embeds.attachments.attachment-file.get-file-type-icon.file-text-icon"
			/>
		);
	if (documentTypes.includes(fileExtension))
		return (
			<FileTextIcon
				size={size}
				data-flx="channel.embeds.attachments.attachment-file.get-file-type-icon.file-text-icon--2"
			/>
		);
	if (archiveTypes.includes(fileExtension))
		return (
			<FileArchiveIcon
				size={size}
				data-flx="channel.embeds.attachments.attachment-file.get-file-type-icon.file-archive-icon"
			/>
		);
	if (audioTypes.includes(fileExtension))
		return (
			<FileAudioIcon
				size={size}
				data-flx="channel.embeds.attachments.attachment-file.get-file-type-icon.file-audio-icon"
			/>
		);
	if (videoTypes.includes(fileExtension))
		return (
			<FileVideoIcon
				size={size}
				data-flx="channel.embeds.attachments.attachment-file.get-file-type-icon.file-video-icon"
			/>
		);
	if (codeTypes.includes(fileExtension))
		return (
			<FileCodeIcon
				size={size}
				data-flx="channel.embeds.attachments.attachment-file.get-file-type-icon.file-code-icon"
			/>
		);
	if (excelTypes.includes(fileExtension))
		return (
			<FileXlsIcon size={size} data-flx="channel.embeds.attachments.attachment-file.get-file-type-icon.file-xls-icon" />
		);
	if (presentationTypes.includes(fileExtension))
		return (
			<FilePptIcon size={size} data-flx="channel.embeds.attachments.attachment-file.get-file-type-icon.file-ppt-icon" />
		);
	return <FileIcon size={size} data-flx="channel.embeds.attachments.attachment-file.get-file-type-icon.file-icon" />;
}
